import { ROLES } from '../../../../constants';

/**
 * Returns active FACILITY_ADMIN memberships derived from a staff member's
 * `locations` array. Backend may return one row per location with a
 * comma-separated `roleName` (legacy "Facility Admin, Teacher") or one row per
 * (location, role); both shapes are deduplicated by locationKey.
 *
 * Used by {@link StaffProvisionToCanvasButton} on the staff profile header to
 * decide which facilities to enqueue for Canvas admin provisioning.
 *
 * @param {Array} locations
 * @returns {Array<{ locationKey: number, locationName: string }>}
 */
/** Accepted roleName / roleDisplayName tokens for Facility Admin (Freedom + portal slugs). */
const FACILITY_ADMIN_ROLE_TOKENS = new Set([
  String(ROLES.FACILITY_ADMIN).trim().toLowerCase(),
  'facility admins',
]);

/** Tokens from comma-separated roleName / roleDisplayName (and displayRoleName alias). */
const roleTokensFromLocation = (loc) =>
  [loc.roleName, loc.roleDisplayName, loc.displayRoleName]
    .filter((value) => value != null && value !== '')
    .flatMap((field) =>
      String(field)
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
    );

const isFacilityAdminLocation = (loc) =>
  roleTokensFromLocation(loc).some((token) => FACILITY_ADMIN_ROLE_TOKENS.has(token));

export const extractFacilityAdminMemberships = (locations) => {
  if (!Array.isArray(locations)) return [];
  const seen = new Map();

  locations.forEach((loc) => {
    if (!loc || loc.active !== true) return;
    if (!isFacilityAdminLocation(loc)) return;

    const locationKey = loc.locationId ?? loc.locationKey;
    if (locationKey == null) return;
    const key = String(locationKey);
    if (seen.has(key)) return;
    seen.set(key, {
      locationKey: Number(locationKey),
      locationName: loc.locationName || loc.name || `Facility ${locationKey}`,
    });
  });

  return Array.from(seen.values());
};
